import { inject, injectable } from 'inversify';
import {
  Army,
  ArmyList,
  ArmyListUnit,
  ArmyListUnitTroop,
  ArmyListUnitOption,
  ConstraintItemType,
  ConstraintType,
  dupArmyListUnit,
  Nullable,
  Organisation,
  TroopCarac,
  Unit,
  UnitCaracType,
  UnitOption,
  UnitOptionAvailability,
  UnitOptionAvailabilityAction,
  UnitOptionOrganisationMode,
  UserFavoriteUnitOption,
} from '@Models';
import { Result } from '@Redux/type';
import {
  ArmyListUnitRepository,
  RequestArmyListUnitSuccess,
} from '@Repository/builder';
import { TYPES } from '@Types';
import { RequestAction, RequestFailure } from '@Repository/RequestAction';
import { v4 as uuidv4 } from 'uuid';
import { ArmyListAction } from './army-list.action';
import { ArmyListUnitOptionAction } from '@Redux/actions/builder/army-list-unit-option.action';
import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ArmyListUnitActionState } from '@Stores/builder/ArmyListUnitState';
import { FavoriteUnitView } from '@Selectors/builder/myArmyList/make/unit/new/view/AvailableUnitCategoryView';
import { AppState } from '@Redux/store';
import { IEntityAction } from '@Redux/IEntityAction';
import armyListSlice from '@Redux/reducers/builder/armyList.slice';
import armyListUnitSlice from '@Redux/reducers/builder/armyListUnit.slice';
import { unitConstraintableElements } from '@Selectors/builder/myArmyList/common/constraints/elements/constrainable/all-constraintable-element';
import { ArmyListUnitUnitOptionAction } from '@Redux/actions';
import {
  getActiveArmyListUnits,
  getArmyListUnit,
} from '@Selectors/builder/myArmyList/make/unit/edit/selector/editSelector';

@injectable()
export class ArmyListUnitAction extends IEntityAction<
  string,
  ArmyListUnit,
  RequestArmyListUnitSuccess
> {
  @inject(TYPES.ArmyListUnitRepository)
  private _repository!: ArmyListUnitRepository;
  @inject(TYPES.Actions.ArmyListAction)
  private _armyListAction!: ArmyListAction;
  @inject(TYPES.Actions.ArmyListUnitOptionAction)
  private _armyListUnitOptionAction!: ArmyListUnitOptionAction;
  @inject(TYPES.Actions.ArmyListUnitUnitOptionAction)
  private _armyListUnitUnitOptionAction!: ArmyListUnitUnitOptionAction;

  public armyListUnitSetSize =
    (size: number): Result<void> =>
    async (dispatch, getState) => {
      const armyListUnit = getArmyListUnit(getState());
      if (!armyListUnit) return;

      const unit = getState().army.entity?.units?.find(
        (u: Unit) => u.id === armyListUnit?.unit_id,
      );
      if (!unit) return;

      dispatch(
        armyListUnitSlice.actions.setArmyListUnit(
          this._armyListUnitOptionAction.updateArmyListUnitPoints(getState(), {
            ...armyListUnit,
            size: size ?? 1,
            army_list_unit_unit_options:
              armyListUnit.army_list_unit_unit_options.map((option) => {
                const unitOption = unit.optionGroups
                  .flatMap((o) => o.options)
                  .find((o) => o.id === option.unit_option_id);

                return {
                  ...option,
                  quantity:
                    unitOption?.is_per_model === true ? size : option.quantity,
                  points:
                    unitOption?.is_per_model === true
                      ? unitOption.points!! * size
                      : option.points,
                };
              }),
          } satisfies ArmyListUnit as ArmyListUnit),
        ),
      );
    };

  public createArmyListUnit = (
    unitId: number,
    armyList: ArmyList,
    army: Army,
    favoriteUnitOption: UserFavoriteUnitOption[] | null = null,
  ): ArmyListUnit | null => {
    const unit = army.units.find((item) => item.id === unitId);
    if (!unit) return null;

    const count =
      armyList.army_list_units.filter((item) => item.unit_id === unitId)
        .length + 1;

    return {
      id: uuidv4(),
      syncId: null,
      errorSync: false,
      isDeleted: false,
      is_enabled: true,
      name: `${unit.name} #${count}`,
      unit_id: unit.id,
      unitName: unit.name,
      organisation_id: unit.principal_organisation_id,
      position:
        armyList.army_list_units.filter(
          (u) => u.organisation_id === unit.principal_organisation_id,
        ).length + 1,
      organisations: unit.allOrganisationIds
        .map((item) => army.organisations.find((o) => o.id === item))
        .filter((item): item is Organisation => !!item),
      size: unit.min_size,
      points: unit.points,
      carac: unit.carac,
      modelRuleUnitTroops: unit.modelRuleUnitTroops.map((item) => ({
        id: uuidv4(),
        modelRuleId: item.modelRuleId,
        unitId: item.unitId,
        troopId: item.troopId,
        name: item.name ?? '',
        info: item.info,
        typeLvl: item.typeLvl,
        modelRuleUnitTroopId: item.id,
        isEnabled: true,
        isVisible: true,
        unitOptionId: null,
        unitOptionChangeModelRuleId: null,
        typeRule: item.typeRule,
        description: item.description,
        isSelected: true,
      })),
      troops: unit.troops.map(
        (item) =>
          ({
            id: uuidv4(),
            name: item.name,
            position: item.position,
            troopId: item.id,
            size: 0,
            points: 0,
            carac: {
              ...item.carac,
            } satisfies TroopCarac as TroopCarac,
          }) satisfies ArmyListUnitTroop as ArmyListUnitTroop,
      ),
      army_list_unit_unit_options: favoriteUnitOption
        ? favoriteUnitOption
            .map((favorite) => {
              const unitOption = unit.optionGroups
                .flatMap((o) => o.options)
                .find((i) => i.id === favorite.unitOptionId);
              if (!unitOption) return null;

              return {
                id: uuidv4(),
                name: unitOption.name,
                unit_option_id: favorite.unitOptionId,
                quantity: favorite.quantity,
                points: favorite.quantity * (unitOption.points ?? 0),
                isDeleted: false,
              } satisfies ArmyListUnitOption as ArmyListUnitOption;
            })
            .filter((item): item is ArmyListUnitOption => !!item)
        : this.setDefaultArmyListUnitUnitOptions(unit, armyList),
      magic: null,
      notes: null,
      mount: null,
      mountId: null,
      updatedAt: 0,
    } satisfies ArmyListUnit as ArmyListUnit;
  };

  createUnitFromFavorite = createAsyncThunk(
    `${this.constructor.name}/createUnitFromFavorite`,
    async ({ unit }: { unit: FavoriteUnitView }, thunkAPI) => {
      thunkAPI.dispatch(this.areLoading(true));

      const armyList = (thunkAPI.getState() as AppState).armyList.entity!!;
      const armyListId = (thunkAPI.getState() as AppState).armyList.id!!;
      const army = (thunkAPI.getState() as AppState).army.entity!!;

      const armyListUnit = this.createArmyListUnit(
        unit.unitId,
        armyList,
        army,
        unit.unitOptions,
      );
      if (!armyListUnit) {
        thunkAPI.dispatch(
          this.haveError('Impossible to create the armyListUnit'),
        );
        thunkAPI.dispatch(this.areLoading(false));
        return;
      }
      armyListUnit.name = unit.name;

      const response = await this._repository.create({
        id: armyListId,
        armyListUnit: armyListUnit,
      });

      if (response instanceof RequestFailure) {
        armyListUnit.errorSync = true;
        thunkAPI.dispatch(
          armyListUnitSlice.actions.setCreationSuccess(armyListUnit),
        );
        thunkAPI.dispatch(
          armyListSlice.actions.armyListUnitCreationSuccess(armyListUnit),
        );
        return;
      }

      const success = response as RequestArmyListUnitSuccess;
      thunkAPI.dispatch(
        armyListUnitSlice.actions.setCreationSuccess(success.payload),
      );
      thunkAPI.dispatch(
        armyListSlice.actions.armyListUnitCreationSuccess(success.payload),
      );
      thunkAPI.dispatch(this.areLoading(false));
    },
  );

  public setDefaultArmyListUnitUnitOptions = (
    unit: Unit,
    armyList: ArmyList,
  ): Array<ArmyListUnitOption> => {
    const armyListUnits = armyList.army_list_units.filter(
      (item) => !item.isDeleted && item.is_enabled,
    );

    const options = unit.optionGroups
      .flatMap((o) => o.options)
      .filter((item) =>
        item.availabilities.some(
          (a) =>
            a.action === UnitOptionAvailabilityAction.AutoSelect &&
            armyListUnits
              .filter((u) => u.unit_id === a.unitId)
              .some((u) =>
                u.army_list_unit_unit_options.some(
                  (ao) => !ao.isDeleted && ao.unit_option_id === a.unitOptionId,
                ),
              ),
        ),
      )
      .map(
        (item) =>
          ({
            id: uuidv4(),
            name: item.name,
            unit_option_id: item.id,
            quantity: 1,
            points: item.points ?? 0,
            isDeleted: false,
          }) satisfies ArmyListUnitOption as ArmyListUnitOption,
      );

    const allConstrainedObjects = unitConstraintableElements(
      unit,
      armyList.armyId,
      armyList.army_organisation_id,
    );
    const constrainedObjects = allConstrainedObjects
      .map((obj) => ({
        ...obj,
        constraints: obj.constraints.filter(
          (c) => c.type === ConstraintType.Min,
        ),
      }))
      .filter(
        (obj) =>
          obj.constraints.length > 0 &&
          obj.type === ConstraintItemType.UnitOptionGroup,
      );

    constrainedObjects.map((constrainedObject) => {
      const child = allConstrainedObjects
        .filter(
          (co) =>
            co.parent.id === constrainedObject.id &&
            co.parent.type === constrainedObject.type,
        )
        .map((obj) =>
          unit.optionGroups
            .flatMap((g) => g.options)
            .find((o) => o.id === obj.id),
        )
        .find(() => true);

      if (!child) return;

      options.push({
        id: uuidv4(),
        name: child.name,
        unit_option_id: child.id,
        quantity: 1,
        points: child.points ?? 0,
        isDeleted: false,
      });
    });

    return options;
  };

  public armyListUnitCreation =
    (unitId: number): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const armyList = getState().armyList.entity!!;
      const armyListId = getState().armyList.id!!;
      const army = getState().army.entity!!;

      const armyListUnit = this.createArmyListUnit(unitId, armyList, army);
      if (!armyListUnit) {
        dispatch(this.haveError('Impossible to create the armyListUnit'));
        dispatch(this.areLoading(false));
        return;
      }

      const response = await this._repository.create({
        id: armyListId,
        armyListUnit: armyListUnit,
      });

      if (response instanceof RequestFailure) {
        const failure = response as RequestFailure;
        dispatch(this.haveError(failure.error.message));
        dispatch(this.areLoading(false));
        return;
      }

      const success = response as RequestArmyListUnitSuccess;
      dispatch(armyListUnitSlice.actions.setCreationSuccess(success.payload));
      dispatch(
        armyListSlice.actions.armyListUnitCreationSuccess(success.payload),
      );
      dispatch(this.areLoading(false));
    };

  public actionToogleEnabledAmyListUnit =
    (armyListUnit: ArmyListUnit, isEnabled: boolean): Result<void> =>
    async (dispatch) => {
      dispatch(
        armyListSlice.actions.updateArmyListUnitEnabled({
          id: armyListUnit.id!!,
          isEnabled: isEnabled,
        }),
      );

      const response = await this._repository.update({
        armyListUnit: {
          ...armyListUnit,
          is_enabled: isEnabled,
        },
      });

      if (response instanceof RequestFailure) {
        dispatch(
          armyListSlice.actions.updateArmyListUnitErrorSync(armyListUnit.id!!),
        );
        return;
      }

      const success = response as RequestArmyListUnitSuccess;
      dispatch(
        armyListSlice.actions.updateArmyListUnitEnabled({
          id: success.payload.id!!,
          isEnabled: success.payload.is_enabled,
        }),
      );
    };

  public actionDeleteArmyListUnit =
    (armyListUnit: ArmyListUnit): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));
      dispatch(armyListSlice.actions.armyListUnitDelete(armyListUnit));

      const response = await this._repository.delete({
        id: armyListUnit.id!!,
        locale: null,
      });

      if (response instanceof RequestFailure) {
        dispatch(
          armyListSlice.actions.updateArmyListUnitErrorSync(armyListUnit.id!!),
        );
        return;
      }

      dispatch(
        armyListSlice.actions.armyListUnitDeleteSuccess(armyListUnit.id!!),
      );
      dispatch(this.areLoading(false));
    };

  public actionCloneArmyListUnit =
    (armyListUnitId: string): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const newArmyListUnitId = uuidv4();
      const state = getState();
      const original = state.armyList.entity?.army_list_units?.find(
        (item) => item.id === armyListUnitId,
      );

      if (!original) throw new Error('ArmyListUnit not found');

      const id = state.armyList.id!!;
      const newArmyListUnit = dupArmyListUnit(original);
      newArmyListUnit.position += 1;
      newArmyListUnit.syncId = newArmyListUnitId;

      const response = await this._repository.create({
        id: id,
        armyListUnit: newArmyListUnit,
      });

      if (response instanceof RequestFailure) {
        newArmyListUnit.errorSync = true;
        dispatch(armyListUnitSlice.actions.setCreationSuccess(newArmyListUnit));
        dispatch(
          armyListSlice.actions.armyListUnitCreationSuccess(newArmyListUnit),
        );
        return;
      }

      const success = response as RequestArmyListUnitSuccess;
      dispatch(armyListUnitSlice.actions.setCreationSuccess(success.payload));
      dispatch(
        armyListSlice.actions.armyListUnitCreationSuccess(success.payload),
      );
      dispatch(this.areLoading(false));
    };

  public actionRequestShowArmyListUnit =
    (armyListUnit: ArmyListUnit): Result<void> =>
    async (dispatch, getState) => {
      const state = getState();
      const units = state.army.entity?.units ?? [];

      const unit = units.find((u: Unit) => u.id === armyListUnit.unit_id);
      if (!unit) return;

      switch (unit.carac?.type) {
        case UnitCaracType.V3:
          dispatch(
            armyListUnitSlice.actions.setRequest({
              armyListUnit: armyListUnit,
              action: ArmyListUnitActionState.ShowV3,
            }),
          );
          break;
        case UnitCaracType.V2:
          dispatch(
            armyListUnitSlice.actions.setRequest({
              armyListUnit: armyListUnit,
              action: ArmyListUnitActionState.ShowV2,
            }),
          );
          break;
        default:
          dispatch(
            armyListUnitSlice.actions.setRequest({
              armyListUnit: armyListUnit,
              action: ArmyListUnitActionState.ShowV1,
            }),
          );
          break;
      }
    };

  public updateArmyListUnitOrganisation = (
    armyListUnit: ArmyListUnit,
    unit: Unit,
    armyListUnits: ArmyListUnit[],
  ): ArmyListUnit => {
    let organisationId = unit.principal_organisation_id;

    const armyListUnitOptionIds = armyListUnit.army_list_unit_unit_options
      .filter((item) => !item.isDeleted)
      .map((item) => item.unit_option_id);

    const armyListUnitChanged = armyListUnits
      .filter(
        (item) =>
          item.unit_id === unit.id && item.is_enabled && !item.isDeleted,
      )
      .filter(
        (item) => item.organisation_id !== unit.principal_organisation_id,
      );

    unit.organisation_changes
      .filter(
        (item) =>
          item.default_organisation_id === unit.principal_organisation_id,
      )
      .filter(
        (item) =>
          !item.max ||
          armyListUnitChanged.filter(
            (change) => change.organisation_id === item.new_organisation_id,
          ).length < item.max,
      )
      .filter(
        (change) =>
          (change.isMin && change.number <= armyListUnit.size) ||
          (change.isMax && change.number >= armyListUnit.size),
      )
      .filter(
        (change) =>
          !change.availabilities.some((avail) =>
            armyListUnitOptionIds.some((id) => id === avail.unitOptionId),
          ),
      )
      .forEach((change) => {
        organisationId = change.new_organisation_id;
      });

    const changeOptions = armyListUnitOptionIds
      .map((item) =>
        unit.optionGroups
          .flatMap((o) => o.options)
          .find((option) => option.id === item),
      )
      .filter((item): item is UnitOption => !!item)
      .filter(
        (item) =>
          item.organisation_mode ===
            UnitOptionOrganisationMode.ChangeUnitOrganisation &&
          item.organisation_id !== null,
      );
    if (
      changeOptions.length > 0 &&
      unit.principal_organisation_id === organisationId
    ) {
      const option = changeOptions[0];
      organisationId = option.organisation_id!!;
    }

    if (armyListUnit.organisation_id !== organisationId) {
      return {
        ...armyListUnit,
        organisation_id: organisationId,
      } satisfies ArmyListUnit as ArmyListUnit;
    }

    return armyListUnit;
  };

  public updateDefaultArmyListUnitUnitOptions =
    (armyListUnit: ArmyListUnit): Result<void> =>
    async (dispatch, getState) => {
      const units = getState().army.entity?.units ?? [];

      const unit = units.find((item: Unit) => item.id === armyListUnit.unit_id);
      if (!unit) return;

      const optionAvailaibilities = unit.optionGroups
        .flatMap((o) => o.options)
        .flatMap((item: UnitOption) => item.availabilityModifiers)
        .filter(
          (item: UnitOptionAvailability) =>
            item.action === UnitOptionAvailabilityAction.AutoSelect,
        );

      const selectedModifiers = optionAvailaibilities.filter((item) =>
        armyListUnit.army_list_unit_unit_options.some(
          (o) => o.unit_option_id === item.modifierId && !o.isDeleted,
        ),
      );

      const unSelectedModifiers = optionAvailaibilities.filter(
        (item) =>
          !armyListUnit.army_list_unit_unit_options.some(
            (o) => o.unit_option_id === item.modifierId && !o.isDeleted,
          ),
      );

      // Stop if no modifier
      if (selectedModifiers.length + unSelectedModifiers.length === 0) return;

      const armyListUnits = getActiveArmyListUnits(getState())
        .filter((item) => item.id != armyListUnit.id)
        .filter(
          (item) =>
            selectedModifiers.some(
              (m: UnitOptionAvailability) => m.unitId === item.unit_id,
            ) ||
            unSelectedModifiers.some(
              (m: UnitOptionAvailability) => m.unitId === item.unit_id,
            ),
        );

      armyListUnits
        .map((item) => {
          selectedModifiers
            .filter((modifier) => modifier.unitId === item.unit_id)
            .filter(
              (modifier) =>
                !item.army_list_unit_unit_options.some(
                  (uo) => uo.unit_option_id === modifier.unitOptionId,
                ),
            )
            .forEach((modifier: UnitOptionAvailability) => {
              const u = units.find((u: Unit) => u.id === modifier.unitId);
              const option = u?.optionGroups
                ?.flatMap((o) => o.options)
                ?.find((o) => o.id === modifier.unitOptionId);

              const quantity = option?.is_per_model ? armyListUnit.size : 1;

              item.army_list_unit_unit_options.push({
                id: uuidv4(),
                name: option?.name ?? '',
                unit_option_id: modifier.unitOptionId!,
                quantity: quantity,
                points: (option?.points ?? 0) * quantity,
                isDeleted: false,
              } satisfies ArmyListUnitOption as ArmyListUnitOption);
            });

          unSelectedModifiers
            .filter((modifier) => modifier.unitId === item.unit_id)
            .forEach((modifier: UnitOptionAvailability) => {
              item.army_list_unit_unit_options =
                this._armyListUnitUnitOptionAction.removeArmyListUnitUnitOption(
                  item.army_list_unit_unit_options,
                  modifier.unitOptionId!,
                );
            });

          return item;
        })
        .forEach((item) => dispatch(this.updateToServer(item)));
    };

  public actionEditArmyListUnit =
    (): Result<void> => async (dispatch, getState) => {
      const state = getState();
      const armyListUnits = state.armyList.entity?.army_list_units ?? [];
      const armyListUnit = state.armyListUnit.entity;
      if (!armyListUnit) return;

      const unit = state.army.entity?.units.find(
        (item) => item.id === armyListUnit.unit_id,
      );

      const armyListUnitUpdated = this.updateArmyListUnitOrganisation(
        armyListUnit,
        unit!!,
        armyListUnits,
      );

      dispatch(this.updateToServer(armyListUnitUpdated));
      dispatch(armyListUnitSlice.actions.cancel());
    };

  public updateToServer =
    (armyListUnit: ArmyListUnit): Result<void> =>
    async (dispatch, getState) => {
      dispatch(armyListSlice.actions.updateArmyListUnit(armyListUnit));

      const response = await this._repository.update({
        armyListUnit: armyListUnit,
      });

      if (response instanceof RequestFailure) {
        dispatch(
          armyListSlice.actions.updateArmyListUnitErrorSync(armyListUnit.id!!),
        );
        return;
      }

      const success = response as RequestArmyListUnitSuccess;
      dispatch(this.updateDefaultArmyListUnitUnitOptions(success.payload));
      dispatch(armyListSlice.actions.updateArmyListUnit(success.payload));
    };

  public moveArmyListUnit =
    ({
      armyListUnitId,
      source,
      destination,
    }: {
      armyListUnitId: string;
      source: number;
      destination: number;
    }): Result<void> =>
    async (dispatch, getState) => {
      const state = getState();

      const armyList = state.armyList.entity;
      if (!armyList) return;

      const armyListUnits = armyList.army_list_units
        .map((item) => {
          if (item.id === armyListUnitId) {
            return {
              ...item,
              position: destination,
            };
          } else if (item.position >= destination) {
            return {
              ...item,
              position: item.position + 1,
            };
          }
          return item;
        })
        .sort((a, b) => a.position - b.position)
        .map((item, index) => {
          return {
            ...item,
            position: index + 1,
          } satisfies ArmyListUnit as ArmyListUnit;
        });

      dispatch(
        this._armyListAction.edit({
          ...armyList,
          army_list_units: armyListUnits,
        }),
      );
    };

  public areLoading(bool: boolean): PayloadAction<boolean> {
    return armyListUnitSlice.actions.setLoading(bool);
  }

  protected fetchDataSuccess(item: ArmyListUnit): PayloadAction<ArmyListUnit> {
    return armyListUnitSlice.actions.setArmyListUnit(item);
  }

  protected cacheDataSuccess(item: ArmyListUnit): PayloadAction<ArmyListUnit> {
    return armyListUnitSlice.actions.setArmyListUnit(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return armyListUnitSlice.actions.setError(message);
  }

  protected getCachedItem({
    entityId,
    locale,
  }: {
    entityId: string;
    locale: Nullable<string>;
  }): Promise<Nullable<ArmyListUnit>> {
    return this._repository.getCachedEntity({
      dataId: entityId,
      locale: locale,
    });
  }

  protected async getItem({
    entityId,
    locale,
  }: {
    entityId: string;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    return this._repository.getArmyListUnit({ id: entityId, locale: locale });
  }
}
